import { useCallback, useEffect, useState } from 'react'
import { Loader2, Lock, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import {
  OFFBOARD_QUESTIONS, blankAnswers, answersToValues, buildAnswers, answeredCount,
  CONFIDENTIAL_TH,
} from '../../lib/resignation.js'
import { decNotice, refreshNotice } from '../../lib/notice.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * แบบสอบถามก่อนพ้นสภาพ — the off-boarding questionnaire, on the employee's own page.
 *
 * 🔒 THE CONFIDENTIALITY LINE IS THE FEATURE. An honest answer about a manager will
 * not be written unless the person believes that manager cannot read it, so the
 * promise is stated at the top, in full, before the first question — not tucked
 * into a footnote. It is kept by GET /resignation_survey/{id} in Xano
 * (`precondition $me.is_hr == true || own`) and by the /api/hr/resignation-survey
 * prefix being on the HR-only allowlist in functions/_middleware.js.
 *
 * ⚠️ CONFIDENTIAL, NOT ANONYMOUS. The answers are attributable and must be — HR
 * cannot act on feedback from nobody. Nothing here says "ไม่ระบุชื่อ", and nothing
 * added later should.
 *
 * Answerable LATER, not only at the moment of resigning: the shell row is created
 * with submitted_at null when the resignation is filed, and this fills it in
 * whenever they come back. Nobody completes an exit questionnaire on the day they
 * quit, and a design that demanded it would simply collect nothing.
 */
export default function OffboardSurvey({ resignationId }) {
  const [values, setValues] = useState(blankAnswers)
  const [comment, setComment] = useState('')
  const [submittedAt, setSubmittedAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await apiGet('/api/me/resignation-survey', { resignation_id: resignationId })
      if (r && r.id) {
        setValues(answersToValues(r.answers))
        setComment(r.comment || '')
        setSubmittedAt(r.submitted_at || null)
        // Not yet answered → open it. This is the one nudge the feature gets, and
        // an accordion the person has to discover is a questionnaire nobody fills in.
        setOpen(!r.submitted_at)
      } else {
        setOpen(true)
      }
    } catch {
      /* a missing survey row is not an error worth showing on this page */
    } finally {
      setLoading(false)
    }
  }, [resignationId])

  useEffect(() => { load() }, [load])

  const set = (key) => (v) => setValues((s) => ({ ...s, [key]: v }))

  async function save() {
    setSaving(true)
    setErr('')
    try {
      await postJson('/api/me/resignation-survey', {
        resignation_id: resignationId,
        answers: buildAnswers(values),
        comment: comment.trim(),
      })
      if (!submittedAt) decNotice('survey_pending', 1)
      setSubmittedAt(Date.now())
      setDone(true)
      setOpen(false)
      refreshNotice()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return null

  const n = answeredCount(values)

  return (
    <div className="mt-3 rounded-xl ring-1 ring-slate-200 bg-slate-50/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left hover:bg-slate-100/60 transition"
      >
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <span className="text-[13px] font-semibold text-slate-700 flex-1">แบบสอบถามก่อนพ้นสภาพ</span>
        {submittedAt ? (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700">
            <CheckCircle2 size={12} /> ตอบแล้ว
          </span>
        ) : (
          <span className="text-[11.5px] text-amber-700 bg-amber-100 rounded-lg px-2 py-0.5">กรุณาตอบแบบสอบถาม</span>
        )}
      </button>

      {open && (
        <div className="px-3.5 pb-3.5">
          {/* Stated in full, before the first question. This is the reason anyone
              would answer honestly, so it is not a footnote. */}
          <div className="flex gap-2 rounded-xl bg-white ring-1 ring-brand-200 px-3 py-2.5 mb-3">
            <Lock size={14} className="text-brand-600 shrink-0 mt-0.5" />
            <p className="text-[12.5px] text-slate-700 leading-relaxed">{CONFIDENTIAL_TH}</p>
          </div>

          <div className="space-y-3.5">
            {OFFBOARD_QUESTIONS.map((q) => (
              <div key={q.key}>
                <div className="text-[12.5px] font-medium text-slate-600 mb-1.5">{q.label}</div>
                {q.type === 'multi' && <MultiChoice options={q.options} value={values[q.key]} onChange={set(q.key)} />}
                {q.type === 'choice' && <SingleChoice options={q.options} value={values[q.key]} onChange={set(q.key)} />}
                {q.type === 'scale' && <Scale min={q.min} max={q.max} value={values[q.key]} onChange={set(q.key)} />}
                {q.type === 'text' && (
                  <textarea
                    rows={2} value={values[q.key]} placeholder={q.placeholder || ''}
                    onChange={(e) => set(q.key)(e.target.value)} className={`${FIELD} resize-y`}
                  />
                )}
              </div>
            ))}

            <div>
              <div className="text-[12.5px] font-medium text-slate-600 mb-1.5">ข้อความอื่นที่อยากบอก</div>
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} className={`${FIELD} resize-y`} />
            </div>
          </div>

          {err && <p className="mt-2 text-[12px] text-rose-600">{err}</p>}
          {done && <p className="mt-2 text-[12px] text-emerald-700">บันทึกเรียบร้อย ขอบคุณสำหรับความเห็นของท่าน</p>}

          <div className="mt-3.5 flex items-center justify-between">
            <span className="text-[11.5px] text-slate-400 tabular-nums">ตอบแล้ว {n}/{OFFBOARD_QUESTIONS.length} ข้อ</span>
            <button
              type="button" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
            >
              {saving && <Loader2 size={13} className="animate-spin" />} {submittedAt ? 'บันทึกการแก้ไข' : 'ส่งแบบสอบถาม'}
            </button>
          </div>
          {/* Partial answers are saved, deliberately. Demanding a complete form
              turns "three useful answers" into nothing at all. */}
          <p className="mt-1.5 text-[11px] text-slate-400 text-right">ตอบไม่ครบก็ส่งได้ และกลับมาแก้ไขภายหลังได้</p>
        </div>
      )}
    </div>
  )
}

const chip = (on) =>
  `px-2.5 py-1 rounded-lg text-[12.5px] ring-1 transition ${
    on ? 'bg-brand-50 text-brand-700 ring-brand-200 font-medium' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
  }`

function MultiChoice({ options, value, onChange }) {
  const list = Array.isArray(value) ? value : []
  const toggle = (o) => onChange(list.includes(o) ? list.filter((x) => x !== o) : [...list, o])
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => toggle(o)} className={chip(list.includes(o))}>{o}</button>
      ))}
    </div>
  )
}

function SingleChoice({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(value === o ? '' : o)} className={chip(value === o)}>{o}</button>
      ))}
    </div>
  )
}

function Scale({ min, max, value, onChange }) {
  const opts = []
  for (let i = min; i <= max; i += 1) opts.push(i)
  return (
    <div className="flex flex-wrap gap-1.5">
      {opts.map((i) => (
        <button
          key={i} type="button"
          onClick={() => onChange(String(value) === String(i) ? '' : String(i))}
          className={`${chip(String(value) === String(i))} tabular-nums min-w-[34px]`}
        >
          {i}
        </button>
      ))}
    </div>
  )
}
